import formReducer, {
  FormReducerAction,
  emptySingleInputField,
  emptyListField,
  formHelperMethodFactory,
  emptyMultiInputField,
} from 'reducers/FormReducer/FormReducer'
import contentBlockFormReducer, {
  ContentBlockFormReducerState,
  emptyContentBlockFormReducerState,
} from './RetailerContentBlockReducer'
import {
  FormReducerSingleTextField,
  FormReducerListField,
  PickByType,
  FormReducerMultiTextField,
  ValidationObject,
  withValidationFactory,
  createFormField,
} from 'reducers/FormReducer/utils'
import { addNewBlockItem, BlockTypes, shiftContentBlockItem, removeBlockItem } from './utils'
import brandLanguageFormReducer, {
  BrandLanguageFormReducerState,
  emptyBrandLanguageFormReducerState,
} from './RetailerBrandLanguageReducer'
import type { RetailersDetailData } from '@contracts'

export interface RetailerFormReducerState {
  retailer_name: FormReducerSingleTextField
  channel: FormReducerSingleTextField
  guidelines: FormReducerListField
  guidelines_shortener: FormReducerListField
  restrictions: FormReducerListField
  content_blocks: ContentBlockFormReducerState[]
  brand_language: BrandLanguageFormReducerState[]
  keyword_priority: FormReducerMultiTextField
  keyword_required: FormReducerMultiTextField
  keyword_optional: FormReducerMultiTextField
  keyword_omit: FormReducerMultiTextField
  keyphrase_required: FormReducerMultiTextField
  keyphrase_optional: FormReducerMultiTextField
}

type PickRetailerStateField<T> = PickByType<RetailerFormReducerState, T>

// Define the keys of the fields that match the correct element types
export type UpdateRetailerSingleInputField = PickRetailerStateField<FormReducerSingleTextField>
export type UpdateRetailerMultiInputField = PickRetailerStateField<FormReducerMultiTextField>
export type UpdateRetailerListField = PickRetailerStateField<FormReducerListField>
export type UpdateRetailerContentBlockField = PickRetailerStateField<ContentBlockFormReducerState>

export const defaultRetailerErrorObject: ValidationObject<'RetailerFormReducerState'> = {}
const withValidation = withValidationFactory(defaultRetailerErrorObject)

export const initialRetailerFormReducerState = (data: RetailersDetailData): RetailerFormReducerState => ({
  retailer_name: withValidation(createFormField('text', data.name || ''), 'retailer_name', ['requiredField'], []),
  channel: withValidation(createFormField('text', data.config?.channel || ''), 'channel', ['requiredField'], []),
  guidelines: createFormField('list', data.config?.content_config?.guidelines || []),
  guidelines_shortener: createFormField('list', data.config?.content_config?.guidelines_shortener || []),
  restrictions: createFormField('list', data.config?.content_config?.restrictions || []),
  content_blocks: (data.content_blocks?.map(block => ({
    id: createFormField('text', block.id || ''),
    title: createFormField('text', block.title || ''),
    task: createFormField('text', block.task || ''),
    formatting: createFormField('select', block.family || ''),
    should_keywords: createFormField('multiSelect', block.keyword_groups?.should || []),
    must_keywords: createFormField('multiSelect', block.keyword_groups?.must || []),
    omit_keywords: createFormField('multiSelect', block.keyword_groups?.omit || []),
    verbatim_must_claims: createFormField('multiSelect', block.keyword_groups?.verbatim_must_claims || []),
    non_verbatim_optional_claims: createFormField(
      'multiSelect',
      block.keyword_groups?.non_verbatim_optional_claims || [],
    ),
    character_limit: createFormField('text', block.character_limit?.toString() || ''),
    position: createFormField('text', block.position?.toString() || ''),
    apply_style_guide:
      block.apply_style_guide !== undefined
        ? createFormField('select', block.apply_style_guide ? 'yes' : 'no')
        : undefined,
    additions: createFormField(
      'text',
      (() => {
        if (block.additions === undefined) {
          return ''
        } else if (typeof block.additions === 'object' && block.additions !== null) {
          return JSON.stringify(block.additions)
        } else {
          return String(block.additions)
        }
      })(),
    ),
    guidelines: createFormField('list', block.guidelines || []),
    guidelines_shortener: createFormField('list', block.guidelines_shortener || []),
    restrictions: createFormField('list', block.restrictions || []),
    keyword_groups: {
      must: createFormField('multiText', block.keyword_groups?.must || []),
      should: createFormField('multiText', block.keyword_groups?.should || []),
      omit: createFormField('multiText', block.keyword_groups?.omit || []),
      verbatim_must_claims: createFormField('multiText', block.keyword_groups?.verbatim_must_claims || []),
      non_verbatim_optional_claims: createFormField(
        'multiText',
        block.keyword_groups?.non_verbatim_optional_claims || [],
      ),
    },
  })) || []) as unknown as ContentBlockFormReducerState[],
  brand_language: (data.brand_language?.map(lang => ({
    region: createFormField('text', lang.region || ''),
    patterns: createFormField('multiText', lang.patterns || []),
    replacement: createFormField('text', lang.replacement || ''),
  })) || []) as unknown as BrandLanguageFormReducerState[],
  keyword_priority: createFormField('multiText', data.retailer_keywords?.priority || []),
  keyword_required: createFormField('multiText', data.retailer_keywords?.required || []),
  keyword_optional: createFormField('multiText', data.retailer_keywords?.optional || []),
  keyword_omit: createFormField('multiText', data.retailer_keywords?.omit || []),
  keyphrase_required: createFormField('multiText', data.retailer_keywords?.keyphrases_required || []),
  keyphrase_optional: createFormField('multiText', data.retailer_keywords?.keyphrases_optional || []),
})

export const emptyRetailerFormReducerState: RetailerFormReducerState = {
  retailer_name: withValidation(emptySingleInputField, 'retailer_name', ['requiredField'], []),
  channel: withValidation(emptySingleInputField, 'channel', ['requiredField'], []),
  guidelines: emptyListField,
  guidelines_shortener: emptyListField,
  restrictions: emptyListField,
  content_blocks: [],
  brand_language: [],
  keyword_priority: emptyMultiInputField,
  keyword_required: emptyMultiInputField,
  keyword_optional: emptyMultiInputField,
  keyword_omit: emptyMultiInputField,
  keyphrase_required: emptyMultiInputField,
  keyphrase_optional: emptyMultiInputField,
}

// action types for reducer
const UPDATE_CONTENT_BLOCK = 'UPDATE_CONTENT_BLOCK'
const TRANSFORM_CONTENT_BLOCKS = 'TRANSFORM_CONTENT_BLOCKS'
const UPDATE_BRAND_LANGUAGE = 'UPDATE_BRAND_LANGUAGE'
const TRANSFORM_BRAND_LANGUAGE = 'TRANSFORM_BRAND_LANGUAGE'

// extend the form reducer to handle retailer specific fields
export type RetailerFormReducerAction =
  | FormReducerAction
  | {
      type: typeof UPDATE_CONTENT_BLOCK
      payload: {
        action: FormReducerAction
        idx: number
      }
    }
  | {
      type: typeof TRANSFORM_CONTENT_BLOCKS
      payload: {
        actionType: 'add' | 'remove' | 'shift'
        idx: number | null
      }
    }
  | {
      type: typeof UPDATE_BRAND_LANGUAGE
      payload: {
        action: FormReducerAction
        idx: number
      }
    }
  | {
      type: typeof TRANSFORM_BRAND_LANGUAGE
      payload: {
        actionType: 'add' | 'remove'
        idx: number | null
      }
    }

const retailerFormReducer = (
  state: RetailerFormReducerState,
  action: RetailerFormReducerAction,
): RetailerFormReducerState => {
  const { type, payload } = action

  switch (type) {
    case 'UPDATE_SINGLE_INPUT':
    case 'UPDATE_MULTI_INPUT':
    case 'UPDATE_LIST':
    case 'UPDATE_SELECT':
    case 'UPDATE_MULTI_SELECT':
    case 'SET_SELECT':
    case 'HANDLE_ERROR':
    case 'HANDLE_WARNING':
    case 'RESET_FORM':
      return formReducer<'RetailerFormReducerState'>(state, action)

    // Delegate the content block state update to the content block reducer
    case UPDATE_CONTENT_BLOCK: {
      const contentBlocks = [...state.content_blocks]
      contentBlocks[action.payload.idx] = contentBlockFormReducer(
        contentBlocks[action.payload.idx],
        action.payload.action,
      )
      return {
        ...state,
        content_blocks: contentBlocks,
      }
    }
    // handle the transformation of the content blocks ()
    case TRANSFORM_CONTENT_BLOCKS: {
      const { actionType, idx } = payload
      let contentBlocks = [...state.content_blocks]
      switch (actionType) {
        case 'add': {
          if (idx !== null) throw new Error('idx should be null for add action')
          contentBlocks = addNewBlockItem<'ContentBlockFormReducerState'>(
            contentBlocks,
            emptyContentBlockFormReducerState,
          )
          break
        }
        case 'remove': {
          if (idx === null) throw new Error('idx should not be null for remove action')
          contentBlocks = removeBlockItem<'ContentBlockFormReducerState'>(contentBlocks, idx)
          break
        }
        case 'shift': {
          if (idx === null) throw new Error('idx should not be null for remove action')
          contentBlocks = shiftContentBlockItem(contentBlocks, idx)
          break
        }
      }
      return {
        ...state,
        content_blocks: contentBlocks,
      }
    }

    // Delegate the brand language state update to the brand language reducer
    case UPDATE_BRAND_LANGUAGE: {
      const brandLanguage = [...state.brand_language]
      brandLanguage[action.payload.idx] = brandLanguageFormReducer(
        brandLanguage[action.payload.idx],
        action.payload.action,
      )
      return {
        ...state,
        brand_language: brandLanguage,
      }
    }
    // handle the transformation of the brand language
    case TRANSFORM_BRAND_LANGUAGE: {
      const { actionType, idx } = payload
      let brandLanguage = [...state.brand_language]
      switch (actionType) {
        case 'add': {
          if (idx !== null) throw new Error('idx should be null for add action')
          brandLanguage = addNewBlockItem<'BrandLanguageFormReducerState'>(
            brandLanguage,
            emptyBrandLanguageFormReducerState,
          )

          break
        }
        case 'remove': {
          if (idx === null) throw new Error('idx should not be null for remove action')
          brandLanguage = removeBlockItem<'BrandLanguageFormReducerState'>(brandLanguage, idx)
          break
        }
      }
      return {
        ...state,
        brand_language: brandLanguage,
      }
    }
    default:
      return state
  }
}

function getRetailerFormHelperFunctions() {
  const formReducerMethods = formHelperMethodFactory<'RetailerFormReducerState'>()

  // Content block form helper functions
  const setContentBlock = (action: FormReducerAction, idx: number): RetailerFormReducerAction => ({
    type: UPDATE_CONTENT_BLOCK,
    payload: { action, idx },
  })

  const shiftContentBlock = (idx: number): RetailerFormReducerAction => ({
    type: TRANSFORM_CONTENT_BLOCKS,
    payload: { actionType: 'shift', idx },
  })

  // Brand language form helper functions
  const setBrandLanguage = (action: FormReducerAction, idx: number): RetailerFormReducerAction => ({
    type: UPDATE_BRAND_LANGUAGE,
    payload: { action, idx },
  })

  const addNewFormBlock = (blockType: BlockTypes): RetailerFormReducerAction => ({
    type: blockType === 'content_blocks' ? TRANSFORM_CONTENT_BLOCKS : TRANSFORM_BRAND_LANGUAGE,
    payload: { actionType: 'add', idx: null },
  })

  const removeFormBlock = (blockType: BlockTypes, idx: number): RetailerFormReducerAction => ({
    type: blockType === 'content_blocks' ? TRANSFORM_CONTENT_BLOCKS : TRANSFORM_BRAND_LANGUAGE,
    payload: { actionType: 'remove', idx },
  })

  return {
    ...formReducerMethods,
    setContentBlock,
    setBrandLanguage,
    shiftContentBlock,
    addNewFormBlock,
    removeFormBlock,
  }
}

export const formHelperFunctions = getRetailerFormHelperFunctions()

export default retailerFormReducer
